import classnames from "classnames";
import React, { type ReactNode } from "react";
import { FormGroup, Label } from "reactstrap";
import type { InputType } from "reactstrap/lib/Input";
import styled from "styled-components";
import FormFeedback from "./FormFeedbackStyled";
import type { FormLabelType } from "./FormLabelRenderer";
import { formGroupWithTooltipStyles } from "src/shared/forms/formrenderers/formGroupWithTooltipStyles";
import { FormTextRenderer } from "src/shared/forms/formrenderers/FormTextRenderer";
import type { IFieldRendererProps } from "src/shared/forms/inputs/IFormInput";

export const StyledFormGroup = styled(FormGroup)`
  ${formGroupWithTooltipStyles};
  position: relative;
  grid-column: start / end !important;
`;

export const StyledInputWrapper = styled.div`
  display: contents;

  > * {
    grid-column: content-start / content-end;
  }
`;

export const StyledTooltipWrapper = styled.div`
  grid-column: tooltip-start / tooltip-end;
  grid-row: 1 / 2;
  align-content: center;
`;

export interface IFormRendererProps {
  label?: FormLabelType;
  formText?: ((value: any) => ReactNode) | ReactNode;
  tooltip?: ReactNode;
  alwaysShowLabel?: boolean;
  disableErrors?: boolean;
  hideLabel?: boolean;
}

export interface IFormRendererCombinedProps<
  FieldValue,
  T extends HTMLElement = HTMLElement,
> extends IFieldRendererProps<FieldValue, T>,
    IFormRendererProps {
  type?: InputType;
  children?: ReactNode;
}

export default <FieldValue, T extends HTMLElement = HTMLElement>({
  type,
  alwaysShowLabel,
  disableErrors,
  hideLabel,
  children,
  formText,
  tooltip,
  input: { value },
  label,
  meta: { error, submitError, submitting },
}: IFormRendererCombinedProps<FieldValue, T>) => {
  if (type === "radio" || type === "checkbox") {
    return (
      <div className="form-check">
        <LabelStyled
          className={classnames({
            "form-check-label": true,
            "is-invalid": !disableErrors && !!error && !!submitError,
          })}
        >
          {children}
          {label}
          {tooltip && <span className="ml-1">{tooltip}</span>}
        </LabelStyled>
        {!disableErrors && (error || submitError) && (
          <FormFeedback>{error || submitError}</FormFeedback>
        )}
        <FormTextRenderer formText={formText} value={value} />
      </div>
    );
  } else {
    let className = "";
    if (alwaysShowLabel || value || type === "select") {
      if (submitting) {
        className = "form-group-readonly";
      }
    } else {
      className = "form-group-hide-label";
    }

    return (
      <StyledFormGroup className={className}>
        <StyledInputWrapper>
          {!hideLabel && label && <LabelStyled>{label}</LabelStyled>}
          {children}
          {!disableErrors && (error || submitError) && (
            <FormFeedback>{error || submitError}</FormFeedback>
          )}
          <FormTextRenderer formText={formText} value={value} />
        </StyledInputWrapper>
        {tooltip && <StyledTooltipWrapper>{tooltip}</StyledTooltipWrapper>}
      </StyledFormGroup>
    );
  }
};

export const LabelStyled = styled(Label)`
  white-space: pre-wrap;
`;
